// OnboardingLayout - Main layout for onboarding pages
// UPDATED: Jan 7, 2026 - Provisioned+archived users redirect to FleetVision instead of inactive page
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useOnboarding } from '../context/OnboardingContext'
import { useAuth } from '../context/AuthContext'
import { useAdmin } from '../context/AdminContext'
import { getRoutePath, STAGES } from '../config/stages'
import ProgressIndicator from '../components/ProgressIndicator'

const FLEETVISION_URL = 'https://fleetvision.insurevision.ai'

const OnboardingLayout = () => {
  const { currentStage, getCurrentStageInfo, isLoading, isSynced, isImpersonating, isDemoMode, isArchived, prospectData, error, refetchUserData } = useOnboarding()
  const { user, logout } = useAuth()
  const { isAdmin, impersonating, demoMode } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()
  const stageInfo = getCurrentStageInfo()

  // Redirect user to correct stage after data syncs from Airtable
  useEffect(() => {
    // Wait until data is synced from backend
    if (!isSynced || isLoading) return

    // DEMO MODE: Skip all redirects - allow viewing any page
    if (isDemoMode) {
      console.log('🎭 Demo mode - skipping stage redirect')
      return
    }

    // If archived BUT provisioned, redirect to FleetVision
    if (isArchived && prospectData.provisioned) {
      console.log('🔄 Archived but provisioned - redirecting to FleetVision')
      window.location.href = FLEETVISION_URL
      return
    }

    // Don't redirect if user is archived - they'll see the inactive message
    if (isArchived) return

    // Safety: redirect legacy stage 01 users to schedule-intro
    if (currentStage === STAGES.USP_REVIEW) {
      console.log('⚠️ Legacy stage 01 detected - redirecting to schedule-intro')
      navigate('/onboarding/schedule-intro', { replace: true })
      return
    }

    // Get the expected route for the current stage
    const expectedPath = getRoutePath(currentStage)

    // If user is not on the correct page for their stage, redirect them
    if (expectedPath && location.pathname !== expectedPath) {
      console.log(`🔄 Redirecting from ${location.pathname} to ${expectedPath} (stage: ${currentStage})`)
      navigate(expectedPath, { replace: true })
    }
  }, [isSynced, isLoading, isArchived, isDemoMode, currentStage, location.pathname, navigate, prospectData.provisioned])

  // Show loading state while syncing with backend
  if (isLoading || !isSynced) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex items-center justify-center ${
        isImpersonating ? 'pt-12' : ''
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-slate-600">
            {isImpersonating
              ? `Loading ${impersonating?.fullName || 'prospect'}'s progress...`
              : 'Loading your progress...'
            }
          </p>
        </div>
      </div>
    )
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex items-center justify-center ${
        isImpersonating ? 'pt-12' : ''
      }`}>
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Heading */}
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Connection Error
            </h1>

            {/* Message */}
            <p className="text-slate-600 mb-6">
              {error}
            </p>

            {/* Retry button */}
            <button
              onClick={refetchUserData}
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 mb-3"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Retrying...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Try Again
                </>
              )}
            </button>

            {/* Sign out link */}
            <button
              onClick={logout}
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Sign out
            </button>

            {/* Support link */}
            <p className="mt-6 text-sm text-slate-500">
              Problem persists?{' '}
              <a href="mailto:sales@insurevision.ai" className="text-accent-600 hover:text-accent-700">
                Contact Sales
              </a>
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Archived + provisioned users get redirected to FleetVision
  // Show a brief loading state while redirect happens
  if (isArchived && prospectData.provisioned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Redirecting to FleetVision...</p>
        </div>
      </div>
    )
  }

  // Show "account inactive" message for archived users (who are NOT provisioned)
  if (isArchived) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            
            {/* Heading */}
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              Account Inactive
            </h1>
            
            {/* Message */}
            <p className="text-slate-600 mb-6">
              Your FleetVision evaluation account is currently inactive. If you'd like to continue your evaluation or have any questions, please get in touch with our team.
            </p>
            
            {/* Contact button */}
            <a
              href="mailto:sales@insurevision.ai"
              className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 transition-colors mb-3"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Sales
            </a>
            
            {/* Sign out link */}
            <button
              onClick={logout}
              className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex flex-col ${
      isImpersonating ? 'pt-12' : ''
    }`}>
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50" style={{
        top: isImpersonating ? '44px' : '0'
      }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-end gap-6">
              <a href="https://insurevision.ai" target="_blank" rel="noopener noreferrer">
                <img 
                  src="/assets/INSUREVISION_AI_LOGO_WH.svg" 
                  alt="InsureVision" 
                  className="h-10"
                />
              </a>
              <span className="font-display font-bold text-lg text-slate-900">
                FleetVision
              </span>
            </div>
            <div className="flex items-center gap-4">
              {isImpersonating && (
                <span className="text-sm text-amber-700 bg-amber-100 px-2 py-1 rounded hidden sm:block">
                  Viewing as: {prospectData.fullName || impersonating?.fullName}
                </span>
              )}
              
              {isAdmin && !isImpersonating && (
                <a
                  href="/admin"
                  className="text-sm font-medium text-brand-600 hover:text-brand-800 transition-colors"
                >
                  Admin
                </a>
              )}
              
              {user && !isImpersonating && (
                <span className="text-sm text-slate-500 hidden sm:block">
                  {user.email}
                </span>
              )}
              
              {!isImpersonating && (
                <button
                  onClick={logout}
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Sign out
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Progress indicator */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <ProgressIndicator currentStage={currentStage} />
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Advanced Automobile Solutions Ltd.
            </p>
            <div className="flex gap-6">
              <a href="https://insurevision.ai/legal/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
                Privacy Policy
              </a>
              <a href="https://insurevision.ai/legal/cookie-policy/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
                Cookie Policy
              </a>
              <a href="mailto:sales@insurevision.ai" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default OnboardingLayout
